import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../constants/api";
import "../../src/styles/Screening.scss";
import Utility from "../utility/Utility";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  LaunchTokenApiCallWithLoader,
  errorUpdate,
} from "../redux/reducers/LoaderSlice";
import AuthService from "../services/AuthService";
import ScreeningHeader from "../components/ScreeningHeader";
import "../../src/styles/components/Loader.scss";
import { Modal } from "react-bootstrap";
import { Trans } from "react-i18next";
import { PAGE } from "../constants/page";
import { PersistState } from "../redux/reducers/PersistSlice";
import { UserState } from "../redux/reducers/UserSlice";
import { updateScreeningStatus } from "src/redux/reducers/ScreeningSlice";
import BeneficiaryBanner from "../components/BeneficiaryBanner";
import { getPractionerIdByAuthId } from "src/services/PractitionerService";
import HttpService from "src/services/HttpService";

const Screening = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [loader, setLoader] = useState(true);
  const [launchUrl, setLaunchUrl] = useState("");
  const patientDetails: any = location.state?.patientDetails;
  const userDetails: any = useAppSelector(PersistState);
  const practitionerDetails: any = useAppSelector(UserState);
  let iframeInstance: any = useRef(null);
  const httpService = HttpService.instance;

  /**
   * isBackButtonClicked variable used to prohibit iframe from rendering
   * on browser back button click
   *
   * TODO: Refactoring required
   * Use updateScreeningStatus.isLoaded fron ScreeningSlice instead
   * localstorage/isBackButtonClicked,
   */
  const isBackButtonClicked: boolean =
    localStorage.getItem("startScreening") === "1" ? false : true;

  // Variables to obtain the launch token
  const grantType = encodeURIComponent(
    "urn:ietf:params:oauth:grant-type:token-exchange"
  );
  const tokenClientId = Utility.env?.REACT_APP_SOF_CLIENT_ID;
  const subjectToken = AuthService.instance.rawAuthToken;
  const fhirBase = encodeURIComponent(Utility.env?.REACT_APP_FHIR_BASE_URL);
  const fhirPatientQuery = encodeURIComponent("?patient=");
  // Launch token payload structure
  const payload =
    "grant_type=:grantType&client_id=:clientId&subject_token=:subjectToken&resource=:fhirBase:fhirPatientQuery:patientId";

  const buildLaunchURL = (
    launchToken: string,
    practitionerId: string
  ): string => {
    // Launch URL variables
    const launchBase = Utility.env?.REACT_APP_KEYCLOAK_URL;
    const launchClientId = Utility.env?.REACT_APP_LAUNCH_CLIENT_ID;
    const selectedLanguage = userDetails?.language
      ? userDetails?.language
      : "en";
    const practitionerGivenName =
      practitionerDetails &&
      practitionerDetails?.userDetails?.given_name?.replaceAll(" ", "-");
    const practitionerFamilyName =
      practitionerDetails &&
      practitionerDetails?.userDetails?.family_name?.replaceAll(" ", "-");
    const practitionerRole =
      practitionerDetails &&
      practitionerDetails?.userDetails?.app_metadata?.organizations?.EODB
        ?.groups[0];
    const practitionerState =
      practitionerDetails &&
      practitionerDetails?.userDetails?.user_metadata?.address?.region?.replaceAll(
        " ",
        "-"
      );
    const practitionerLocality =
      practitionerDetails &&
      practitionerDetails?.userDetails?.user_metadata?.address?.locality.split(
        ","
      );
    const practitionerDistrict = (
      practitionerLocality.length > 1
        ? practitionerLocality[1]
        : practitionerLocality[0]
    ).replaceAll(" ", "-");
    const beneficiaryType = patientDetails?.identifier
      ? patientDetails.identifier[0].assigner.display
      : "ID";
    const beneficiaryValue = patientDetails?.identifier
      ? patientDetails.identifier[0].value
      : "unavailable";

    const redirectUri =
      Utility.env?.REACT_APP_PCP_LAUNCH_REDIRECT_URL +
      `&practitionerState=${practitionerState}&practitionerDistrict=${practitionerDistrict}&practitionerRole=${practitionerRole}&practitionerGivenName=${practitionerGivenName}&practitionerFamilyName=${practitionerFamilyName}&beneficiaryType=${beneficiaryType}&beneficiaryValue=${beneficiaryValue}&timer=${process.env.REACT_APP_SESSION_EXPIRY_MINUTES}&practitionerId=${practitionerId}`;
    const launchRedirectURL = encodeURIComponent(
      redirectUri.replace(":lang", selectedLanguage)
    );
    const iss = encodeURIComponent(Utility.env?.REACT_APP_PCP_LAUNCH_ISS);
    const url = `${launchBase}/realms/h/sof-broker/asha/launch?client_id=${launchClientId}&redirect_uri=${launchRedirectURL}&iss=${iss}&launch=${launchToken}`;
    return url;
  };

  const handleIframeCommunication = (e: any) => {
    if (e?.data?.message === "contentLoadError") {
      window.removeEventListener("message", handleIframeCommunication, false);
      // back to patient profile page
      navigate(
        PAGE.VIEW_PATIENT_DETAILS.replace(
          ":id",
          location.state.patientDetails.id
        )
      );
    }
  };

  const handleOnLoad = (e: any) => {
    // incase needs to manupulate header/footer can be handle here
    if (localStorage.getItem("startScreening") === "1") {
      dispatch(updateScreeningStatus({ screening: true, loaded: true }));
      setTimeout(() => {
        setLoader(false);
        e?.target.contentWindow.postMessage(
          {
            func: "childFunc",
            message: "startScreening",
          },
          "*"
        );
      }, 1500);
    }
    // remove event if already exist
    window.removeEventListener("message", handleIframeCommunication, false);
    //add event
    window.addEventListener("message", handleIframeCommunication, false);
  };

  const activateIframeTimer = () => {
    (
      document.getElementById("screeningFrame") as any
    )?.contentWindow.postMessage(
      {
        func: "childFunc",
        message: "activateTimer",
      },
      "*"
    );
  };

  const fetchUserDetails = async () => {
    const requestDetails = {
      url: API.LAUNCH_TOKEN,
      method: "POST",
      requestData: payload
        .replace(":grantType", grantType)
        .replace(":clientId", tokenClientId)
        .replace(":subjectToken", subjectToken)
        .replace(":fhirBase", fhirBase)
        .replace(":fhirPatientQuery", fhirPatientQuery)
        .replace(":patientId", patientDetails?.id),
    };

    try {
      const resp = await dispatch(LaunchTokenApiCallWithLoader(requestDetails));
      let practitionerId = "";
      if (!isBackButtonClicked) {
        try {
          const practitioner = await getPractionerIdByAuthId(
            AuthService.instance?.user?.auth0_id.replace("auth0|", "")
          );
          practitionerId = practitioner?.entry[0]?.resource?.id;
        } catch (error) {
          practitionerId = AuthService.instance?.user?.sub; //keycloak id
        }
        setLaunchUrl(
          buildLaunchURL(resp.payload?.access_token, practitionerId)
        );
      }
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  useEffect(() => {
    if (iframeInstance?.current) {
      iframeInstance?.current?.contentWindow?.postMessage(
        {
          func: "childFunc",
          message: "changeLanguage",
          language: userDetails?.language,
        },
        "*"
      );
    }
  }, [userDetails?.language]);

  useEffect(() => {
    if (!loader) {
      // localStorage.removeItem("startScreening");
    }
  }, [loader]);

  useEffect(() => {
    if (userDetails.header) {
      document.body.classList.add("screening-body-without-header");
    } else {
      document.body.classList.add("screening-body");
    }
    document.removeEventListener("activateIframeTimer", activateIframeTimer);
    document.addEventListener("activateIframeTimer", activateIframeTimer);
    fetchUserDetails();
    return () => {
      document.body.classList.remove(
        "screening-body",
        "screening-body-without-header"
      );
    };
  }, []);

  return (
    <div>
      <ScreeningHeader />
      <div className="beneficiary-banner-section">
        <div className="container pt-0 pb-0">
          <BeneficiaryBanner beneficiaryData={patientDetails} />
        </div>
      </div>
      <div className="pcpIframe">
        {launchUrl !== "" && loader ? (
          <Modal show={loader} dialogClassName="loaderModal" centered>
            <div className="loaderInfo">
              <div className="loaderBox">
                <div className="c-els-loader__loading-bar"></div>
                <div className="c-els-loader__loading-bar"></div>
                <div className="c-els-loader__loading-bar"></div>
                <span className="loader__accessible-message u-els-hide-visually">
                  Loading page
                </span>
              </div>
              <p>
                <Trans i18nKey={"pcp_loading_msg"}></Trans>
              </p>
            </div>
          </Modal>
        ) : null}
        {patientDetails && launchUrl !== "" && !isBackButtonClicked && (
          <iframe
            onLoad={handleOnLoad}
            id="screeningFrame"
            src={launchUrl}
            className="screening-frame"
            ref={iframeInstance}
          ></iframe>
        )}
      </div>
    </div>
  );
};

export default Screening;
